import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CVEChart = ({ imageData }) => {
  if (!imageData) return null;

  // Generate time series data based on actual CVE published dates
  const generateCVETrend = (cveList, sinceDate) => {
    // Use cve_graph_since date or default to 30 days ago
    const startDate = sinceDate ? new Date(sinceDate) : new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    const now = new Date();
    
    // Sort CVEs by published date
    const sortedCVEs = [...cveList].sort((a, b) => new Date(a.publishedDate) - new Date(b.publishedDate));
    
    // Count CVEs that existed before the start date (baseline count)
    const baselineCVECount = sortedCVEs.filter(cve => new Date(cve.publishedDate) < startDate).length;
    
    // Calculate days from start date to now
    const daysDiff = Math.ceil((now - startDate) / (1000 * 60 * 60 * 24));
    const totalDays = Math.max(1, daysDiff);
    
    const dates = [];
    const data = [];
    
    // Generate date labels from start date to now
    for (let i = totalDays - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      dates.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      
      // Count new CVEs published from start date up to this date
      const newCVECount = sortedCVEs.filter(cve => {
        const cveDate = new Date(cve.publishedDate);
        return cveDate <= date && cveDate >= startDate;
      }).length;
      
      // Add baseline count to show total accumulated CVEs
      data.push(baselineCVECount + newCVECount);
    }

    return { dates, data };
  };

  // Generate trends for both datasets using the cve_graph_since date
  const sinceDate = imageData.cve_graph_since;
  const koalaTrend = generateCVETrend(imageData.koala.cves, sinceDate);
  const dockerhubTrend = generateCVETrend(imageData.dockerhub.cves, sinceDate);
  
  // Ensure both datasets use the same date range (the longer one)
  const maxLength = Math.max(koalaTrend.dates.length, dockerhubTrend.dates.length);
  let commonDates, koalaData, dockerhubData;
  
  if (koalaTrend.dates.length >= dockerhubTrend.dates.length) {
    commonDates = koalaTrend.dates;
    koalaData = koalaTrend.data;
    // Extend dockerhub data to match
    dockerhubData = [...dockerhubTrend.data];
    while (dockerhubData.length < maxLength) {
      dockerhubData.unshift(dockerhubData[0] || 0);
    }
  } else {
    commonDates = dockerhubTrend.dates;
    dockerhubData = dockerhubTrend.data;
    // Extend koala data to match
    koalaData = [...koalaTrend.data];
    while (koalaData.length < maxLength) {
      koalaData.unshift(koalaData[0] || 0);
    }
  }

  // CVE by Severity Bar Chart Data
  const getCVEBySeverity = (cveList) => {
    const severityCount = {
      critical: cveList.filter(cve => cve.severity === 'CRITICAL').length,
      high: cveList.filter(cve => cve.severity === 'HIGH').length,
      medium: cveList.filter(cve => cve.severity === 'MEDIUM').length,
      low: cveList.filter(cve => cve.severity === 'LOW').length,
    };
    return severityCount;
  };

  const koalaSeverity = getCVEBySeverity(imageData.koala.cves);
  const dockerhubSeverity = getCVEBySeverity(imageData.dockerhub.cves);

  const barChartData = {
    labels: ['Critical', 'High', 'Medium', 'Low'],
    datasets: [
      {
        label: 'Koala Hardened',
        data: [koalaSeverity.critical, koalaSeverity.high, koalaSeverity.medium, koalaSeverity.low],
        backgroundColor: '#EB1C45',
        borderColor: '#EB1C45',
        borderWidth: 1,
      },
      {
        label: 'DockerHub Alternative',
        data: [dockerhubSeverity.critical, dockerhubSeverity.high, dockerhubSeverity.medium, dockerhubSeverity.low],
        backgroundColor: '#666666',
        borderColor: '#666666',
        borderWidth: 1,
      },
    ],
  };

  const lineChartData = {
    labels: commonDates,
    datasets: [
      {
        label: 'Koala Hardened',
        data: koalaData,
        borderColor: '#EB1C45',
        backgroundColor: 'rgba(235, 28, 69, 0.1)',
        fill: false,
        tension: 0.1,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: 'DockerHub Alternative',
        data: dockerhubData,
        borderColor: '#666666',
        backgroundColor: 'rgba(102, 102, 102, 0.1)',
        fill: false,
        tension: 0.1,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#FFFFFF',
          font: {
            family: 'Montserrat, -apple-system, BlinkMacSystemFont, sans-serif',
            size: 12,
          },
        },
      },
      tooltip: {
        backgroundColor: '#000000',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        borderColor: '#333333',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: {
          color: '#333333',
        },
        ticks: {
          color: '#999999',
          font: {
            family: 'Montserrat, -apple-system, BlinkMacSystemFont, sans-serif',
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#333333',
        },
        ticks: {
          color: '#999999',
          font: {
            family: 'Montserrat, -apple-system, BlinkMacSystemFont, sans-serif',
            size: 11,
          },
        },
      },
    },
  };

  return (
    <div className="cve-charts">
      <div className="chart-section">
        <h3>CVEs by Severity</h3>
        <div className="chart-container">
          <Bar data={barChartData} options={chartOptions} />
        </div>
      </div>
      
      <div className="chart-section">
        <h3>Total CVEs Over Time</h3>
        <div className="chart-container">
          <Line data={lineChartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
};

export default CVEChart;